/**
 * HR acting as someone (TIME_OFF_PLAN, screens 5): the picker beside HR's name, and the banner
 * shown on every screen while it is on.
 *
 * A face, no rules. The API honours acting-as only for HR, refuses a missing note, logs the
 * start, the stop and every write, and tells the person about each write. The person is chosen
 * from the roster, never typed, and a note is required before anything starts.
 */
import type { PersonRef, PersonSummary } from "@scheduler/shared";
import { useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "./api/scheduler";
import { actingAs, setActingAs } from "./auth/actingAs";

export function ActAsPicker({ me }: { me: PersonSummary }) {
  const api = useSchedulerApi();
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState<PersonRef[]>([]);
  const [ajeraKey, setAjeraKey] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || people.length) return;
    api.people().then((list) => setPeople(list.filter((p) => p.id !== me.id)), () => setError("Could not load the roster."));
  }, [api, open, people.length, me.id]);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const person = await api.actAsStart(ajeraKey, note.trim());
      setActingAs({ ajeraKey: person.ajeraKey, name: person.name, note: note.trim() });
      window.location.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not start.");
      setBusy(false);
    }
  }

  if (!open) {
    return <button type="button" className="ghost" onClick={() => setOpen(true)}>Act as someone…</button>;
  }
  return (
    <span className="actpick">
      <select aria-label="Person to act as" value={ajeraKey} onChange={(e) => setAjeraKey(e.target.value)}>
        <option value="">Choose from the roster…</option>
        {people.map((p) => <option key={p.ajeraKey} value={p.ajeraKey}>{p.name}</option>)}
      </select>
      <input type="text" aria-label="Why (required)" aria-describedby="act-as-note-hint" placeholder="Why?" maxLength={500}
        value={note} onChange={(e) => setNote(e.target.value)} />
      {/* Visible, not just a placeholder that vanishes on the first keystroke: they DO see it (H7). */}
      <span id="act-as-note-hint" className="hint">Required. The person you act as will see this note.</span>
      <button type="button" className="primary" disabled={!ajeraKey || !note.trim() || busy} onClick={() => void start()}>Start</button>
      <button type="button" className="ghost" onClick={() => setOpen(false)}>Cancel</button>
      {error ? <span className="error" role="alert">{error}</span> : null}
    </span>
  );
}

/**
 * `wfh`: on the Work from home tab, where a mark made while acting is logged but tells nobody
 * (19 Oct 2026), so the usual "they will be told" would be untrue there.
 */
export function ActAsBanner({ me, wfh = false }: { me: PersonSummary; wfh?: boolean }) {
  const api = useSchedulerApi();
  const [busy, setBusy] = useState(false);
  if (!me.actingAs) return null;

  async function stop() {
    setBusy(true);
    const current = actingAs();
    try {
      // Logged as the real HR person. If the log call fails, acting-as still stops here:
      // being stuck as someone else is the worse failure.
      if (current) await api.actAsStop(current.ajeraKey, current.note);
    } catch {
      // Nothing more to do; the tab stops sending the header below.
    }
    setActingAs(null);
    window.location.reload();
  }

  return (
    <div className="actbanner" role="status">
      <span>
        <strong>You are acting as {me.name}</strong> ({me.actingAs.by.name}, HR). {wfh ? "Marks you make here are logged; they are not told." : "They will be told about anything you change."}
      </span>
      <button type="button" className="ghost no" disabled={busy} onClick={() => void stop()}>Stop</button>
      <span className="why">Note: {me.actingAs.note}</span>
    </div>
  );
}
